import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

import MuiCircularProgress from '@material-ui/core/CircularProgress';
import { useTheme, withStyles } from '@material-ui/core/styles';

import { buildModalQuery } from 'Consts/ModalQueries';
import relationships from 'Consts/Relationships';
import { openModal } from 'Helpers/ModalHelper';
import { openTimeline } from 'Resources/CalendarEvent';
import modalConsts from 'Consts/ModalNames';

import SequenceBar from 'Components/UI/SequenceBar';

import useRefDimensions from 'Hooks/useRefDimensions';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useWindowSize from 'Hooks/useWindowSize';

import frRelationships from 'facet-rest/src/consts/relationships';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import ProjectedMeetingSequenceBarEvent
  from 'Components/AdvisorApp/Viewport/MissionControl/Dashboard/SequenceBar/ProjectedMeetingSequenceBarEvent';
import { pickImplementation } from 'Helpers/Affordances';
import fr from 'facet-rest';
import { copyToClipboard } from 'Helpers/Utils';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import { clientSchedulerUri } from 'Consts/clientUris';
import { GetAppUrl } from 'Consts/sharedUris';
import moment from 'moment-timezone';
import { useMissionControlURL } from 'Hooks/useMissionControlURL';
import SequenceBarButton from './SequenceBarButton';
import SequenceBarEvent from './SequenceBarEvent';
import useUserContext from '../../../../../../Context/UserContext';

const styles = theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(6),
  },
  rootLoading: {
    justifyContent: 'center',
  },
  sequenceBar: {
    width: '100%',
    height: '100%',
  },
  sequenceBarCenter: {
    width: theme.dimensions.sm,
  },
  scheduleNextItem: {
    width: '100%',
  },
  progressCircle: {
    color: theme.palette.facet.primaryGray,
  },
  scheduleNextItemFooterText: {
    color: theme.palette.secondary.main,
    fontWeight: theme.typography.fontWeightBold,
  },
  nextProjectedLabel: {
    ...theme.typography.h6,
    marginBottom: 'unset',
  },
  projectedMeetingFooterText: {
    color: theme.palette.facet.primaryGray,
  },
});

function MissionControlSequenceBar({
  calendarEventId,
  className,
  classes,
  loading,
  meetingLoading,
  pastEvents,
  futureEvents,
  projectedMeetings,
  areMeetingsCategorized,
}) {
  const theme = useTheme();
  const { householdResource } = useSimpleHousehold();
  const { agentResource } = useUserContext();
  const [rootRef, rootDimensions] = useRefDimensions(useWindowSize());
  const { logEvent } = useEventTrackingLogEvent({ ref: rootRef });
  const allDisabled = meetingLoading;
  const isMissionControlExperimentalEnabled = householdResource
    && householdResource.relationship(relationships.missionControl);
  const shouldAddScheduleNextItem = futureEvents.length < 2;
  const leftItemCount = pastEvents.length;
  const rightItemCount = futureEvents.length + (shouldAddScheduleNextItem ? 1 : 0) - 1; // Subtract 1 since the first item goes in the center.
  const maxSideItems = Math.max(leftItemCount, rightItemCount);
  const missionControlURL = useMissionControlURL();

  const itemWidthCalc = pickImplementation(householdResource, {
    [frRelationships.default.name]: () => {
      if (rootDimensions.width < 1440) {
        return rootDimensions.width / 2 * maxSideItems;
      }
      return (rootDimensions.width - theme.dimensions.sm) / (2 * maxSideItems);
    },
  });

  const itemWidth = itemWidthCalc();
  const { leftWidth } = pickImplementation(householdResource, {
    [frRelationships.default.name]: {
      leftWidth: rootDimensions.width < 1440 ? '100%' : itemWidth,
    },
  });

  const itemStyle = Number.isFinite(rootDimensions.width) // This isn't set on the first render.
    ? { width: itemWidth }
    : {};
  const nextEventItemStyle = Number.isFinite(rootDimensions.width) // This isn't set on the first render.
    ? { width: 2 * itemWidth } // The center item is double wide.
    : {};

  function getPastEventIconButtonProps(event) {
    if (isMissionControlExperimentalEnabled) {
      return {
        test: 'mc-sequence-bar-item',
        component: Link,
        onClick: () => logEvent(
          'Clicked on Mission Control Sequence Bar Past Event',
          { calendarEventId: event.id() },
        ),
        to: () => missionControlURL({ meetingSelfLink: event.link('self').url() }),
      };
    }

    return {
      test: 'mc-sequence-bar-item',
      onClick: () => openTimeline(event),
    };
  }

  const futureItems = futureEvents.map((event, i) => (isMissionControlExperimentalEnabled ? (
    <SequenceBarEvent
      disabled={i !== 0 && meetingLoading}
      eventResource={event}
      isNextEvent={i === 0}
      displayEditButton={event.attr('calendarEventId') !== ''}
      key={`future-event.${event.id()}`}
      style={i === 0 ? nextEventItemStyle : itemStyle}
      variant={
        calendarEventId === event.id()
          ? 'selectedMeeting'
          : 'futureMeeting'
      }
      IconButtonProps={{
        test: 'mc-sequence-bar-item',
        component: Link,
        onClick: () => logEvent(
          'Clicked on Mission Control Sequence Bar Future Event',
          { calendarEventId: event.id() },
        ),
        to: () => missionControlURL({ meetingSelfLink: event.link('self').url() }),
      }}
    />
  ) : (
    <SequenceBarEvent
      disabled={allDisabled || i > 0}
      eventResource={event}
      isNextEvent={i === 0}
      key={`future-event.${event.id()}`}
      style={itemStyle}
      variant="futureMeeting"
      IconButtonProps={{
        onClick: () => openModal(
          buildModalQuery(modalConsts.advisorNote, {
            link: event
              .relationship('googleCalendar')
              .link('related')
              .url(),
          }),
        ),
      }}
    />
  )));

  const scheduleNextItems = pickImplementation(householdResource, {
    [frRelationships.default.name]: projectedMeetings?.resources().list().slice(0, 3).map((projectedMeeting, index) => (
      <ProjectedMeetingSequenceBarEvent
        areMeetingsCategorized={areMeetingsCategorized}
        resource={projectedMeeting}
        style={itemStyle}
        count={index + 1}
      />
    )) || [],
  });

  futureItems.push(...scheduleNextItems);

  const copyLink = pickImplementation(agentResource, {
    [fr.relationships.default.name]: async () => {
      try {
        const availabilityPeriodLinkCollection = await householdResource
          .relationship(fr.relationships.buildSchedulerLink.name)
          .link('related')
          .get();

        const newAvailabilityPeriodLink = availabilityPeriodLinkCollection.create();
        const schedulerLink = `${GetAppUrl()}${clientSchedulerUri}&link=`;
        newAvailabilityPeriodLink.attr('sendEmail', 'false');
        newAvailabilityPeriodLink.attr('timezone', moment.tz.guess());
        newAvailabilityPeriodLink.attr('clientUrl', schedulerLink);
        newAvailabilityPeriodLink.attr('blockType', 'planning');
        newAvailabilityPeriodLink.attr('meetingType', 'planning');
        newAvailabilityPeriodLink.attr('cadence', 'cadence');
        newAvailabilityPeriodLink.attr('duration', '45');
        newAvailabilityPeriodLink.attr('bufferDuration', '0');

        const [createdAvailabilityPeriodLinkResource] = await newAvailabilityPeriodLink.submit();
        copyToClipboard(createdAvailabilityPeriodLinkResource.attr('link'));
        displaySuccess('Copied link to clipboard');
      } catch (err) {
        displayErrorMessage(err, 'Failed to copy link');
      }
    },
  });

  const copyScheduleLinkItem = pickImplementation(householdResource, {
    [frRelationships.default.name]: (
      <SequenceBarButton
        style={itemStyle}
        onClick={() => {
          logEvent('Clicked on Share Schedule Link button');
          copyLink();
        }}
      />
    ),
  });
  futureItems.push(copyScheduleLinkItem);

  return (
    <div
      className={classNames(
        classes.root,
        {
          [classes.rootLoading]: loading,
        },
        className,
      )}
      ref={rootRef}
    >
      {loading ? (
        <MuiCircularProgress
          className={classes.progressCircle}
          color="inherit"
        />
      ) : (
        <SequenceBar
          classes={{
            root: classes.sequenceBar,
            center: classes.sequenceBarCenter,
          }}
          left={pastEvents.map(event => (
            <SequenceBarEvent
              disabled={allDisabled}
              eventResource={event}
              key={`past-event.${event.id()}`}
              style={{ width: leftWidth }}
              variant={
                calendarEventId === event.id()
                  ? 'selectedMeeting'
                  : 'completedMeeting'
              }
              IconButtonProps={getPastEventIconButtonProps(event)}
            />
          ))}
          center={[futureItems[0]]} // Head
          right={futureItems.slice(1)} // Tail
        />
      )}
    </div>
  );
}

MissionControlSequenceBar.defaultProps = {
  calendarEventId: null,
  className: '',
  loading: false,
  meetingLoading: false,
  projectedMeetings: null,
};

MissionControlSequenceBar.propTypes = {
  calendarEventId: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  meetingLoading: PropTypes.bool,
  pastEvents: PropTypes.arrayOf(PropTypes.object).isRequired,
  futureEvents: PropTypes.arrayOf(PropTypes.object).isRequired,
  projectedMeetings: PropTypes.object,
  areMeetingsCategorized: PropTypes.bool.isRequired,
};

export default withStyles(styles, { name: 'MissionControlSequenceBar' })(
  MissionControlSequenceBar,
);
